import { filter, find, get, includes, isNil, map, pick, values } from 'lodash';

import { MAP_TYPES } from './Maps';

/**
 * @typedef {object} Feature
 * @typedef {object} Point
 */

/**
 * Check if the layers are overrlayed or not.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True if the layers are overlayed, false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const areFeature = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'isFeature', false);
};

/**
 * Check if the layers are overrlayed or not.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True if the layers are overlayed, false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const areLayersOverlayed = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'layersOverlayed', false);
};

/**
 * Checks if the map buttons are to be shown.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} False if buttons are not to be shown, true otherwise.
 * @throws {Error} If atlas is null or undefined.
 */
export const arePopupInformation = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'selectedButtons.PopupInformation', false);
};

/**
 * Checks if the map buttons are to be shown.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} False if buttons are not to be shown, true otherwise.
 * @throws {Error} If atlas is null or undefined.
 */
export const areViewHtml = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'selectedButtons.ViewHtml', false);
};

/**
 * Get map bbox.
 *
 * @param {object} mapObject - The map object.
 * @returns {number[]} The bbox of the map or an empty array if it's not defined.
 * @throws {Error} If map is null or undefined.
 */
export const getBBox = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'mapLocation.bbox', []);
};
/**
 * Get the background selected for the map.
 *
 * @param {object} mapObject - The map object.
 * @returns {string} The string of the background that where selected for the map or an a string with the value #ddd.
 * @throws {Error} If map is null or undefined.
 */
export const getBackground = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'backgroundColor', '#ddd');
};

/**
 * Get the base maps selected for the map.
 *
 * @param {object} mapObject - The map object.
 * @returns {Array} The array of base map names that where selected for the map or an empty array.
 * @throws {Error} If map is null or undefined.
 */
export const getBaseMaps = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'baseMaps', []);
};
/**
 * Get the object with the base maps that need a different transparency value.
 *
 * @param {object} mapObject - The map object.
 * @returns {object[]} An array containing the base maps that need a different transparency value, or empty array if not found.
 * @throws {Error} If map is null or undefined.
 */
export const getBaseMapsTransparency = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'baseMapsTransparency', []);
};

/**
 * Get the array with the base maps that need different zoom values.
 *
 * @param {object} mapObject - The map object.
 * @returns {object[]} An array containing the base maps that need different zoom values, or empty array if not found.
 * @throws {Error} If map is null or undefined.
 */
export const getBaseMapsZoom = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'baseMapsZoom', []);
};

/**
 * Get map description.
 *
 * @param {object} mapObject - The map object.
 * @returns {string} The description of the map or an empty string if not found.
 * @throws {Error} If map is null or undefined.
 */
export const getDescription = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'description', '');
};

/**
 * Get Esri Web Map ID.
 *
 * @param {object} mapObject - The map object.
 * @returns {string} The Esri Web Map ID or empty string if not found.
 * @throws {Error} If map is null or undefined.
 */
export const getEsriWebMapId = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'esriWebMapId', '');
};

/**
 * Gets the map id.
 *
 * @param {object} mapObject - The map object.
 * @returns {string} The id for the map or empty string if not found.
 * @throws {Error} If map is null or undefined.
 */
export const getId = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, '_id', '');
};

/**
 * Get map image URL.
 *
 * @param {object} mapObject - The map object.
 * @returns {string} The map image URL or empty string if not found.
 * @throws {Error} If map is null or undefined.
 */
export const getImageURL = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'imageUrl', '');
};

/**
 * Get the layers contained in the map.
 *
 * @param {object} mapObject - The map object.
 * @returns {Array} The array of layer id's that the map shows or an empty array if not found.
 * @throws {Error} If map is null or undefined.
 */
export const getLayers = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'layers', []);
};
/**
 * Gets the layers of the map in array form.
 *
 * @param {object} mapObject - The map object.
 * @param {Array} layers - The array of layers objects.
 * @param {boolean} [lookForLayers=true] - True if searching for layers, false is searching in base maps.
 * @returns {object[]} The layers/base maps of the map in array form.
 * @throws {Error} If map is null or undefined.
 */
export const getLayerObjectsArray = (mapObject, layers, lookForLayers = true) => {
  if (isNil(mapObject)) throw new Error('map not defined');
  const layersToLook = lookForLayers ? getLayers(mapObject) : getBaseMaps(mapObject);
  return filter(
    map(layersToLook, layerId => find(layers, ['_id', layerId])),
    layer => !isNil(layer)
  );
};
/**
 * Get the object with the layers that need a different transparency value.
 *
 * @param {object} mapObject - The map object.
 * @returns {object[]} An array containing the layers that need a different transparency value, or empty array if not found.
 * @throws {Error} If map is null or undefined.
 */
export const getLayersTransparency = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'layersTransparency', []);
};

/**
 * Get the array with the layers that need different zoom values.
 *
 * @param {object} mapObject - The map object.
 * @returns {object[]} An array containing the layers that need different zoom values, or empty array if not found.
 * @throws {Error} If map is null or undefined.
 */
export const getLayersZoom = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'layersZoom', []);
};
/**
 * Gets the log url for the map logo.
 *
 * @param {object} mapObject - The map object.
 * @returns {string} The logo url for the map logo. Empty string if not found.
 * @throws {Error} If map is null or undefined.
 */
export const getLogoURL = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'logoUrl', '');
};

/**
 * Get map location.
 *
 * @param {object} mapObject - The map object.
 * @returns {Feature<Point>} The location of the map or an empty object if it's not defined.
 * @throws {Error} If map is null or undefined.
 */
export const getMapLocation = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'mapLocation', {});
};

/**
 * Get map name.
 *
 * @param {object} mapObject - The map object.
 * @returns {string} The name of the map or empty string if not found.
 * @throws {Error} If map is null or undefined.
 */
export const getName = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'name', '');
};

/**
 * Gets the page url for the map logo.
 *
 * @param {object} mapObject - The map object.
 * @returns {string} The page url for the map logo. Empty string if not found.
 * @throws {Error} If map is null or undefined.
 */
export const getPageURL = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'pageUrl', '');
};
/**
 * Get the public id for the map.
 *
 * @param {object} mapObject - The map object.
 * @returns {string} The public id for the map or an empty string if not found.
 * @throws {Error} If map is null or undefined.
 */
export const getPublicId = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'publicId', '');
};

/**
 * Get the public id for the map.
 *
 * @param {object} mapObject - The map object.
 * @returns {string} The public id for the map or an empty string if not found.
 * @throws {Error} If map is null or undefined.
 */
export const getPublicPrivateId = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'publicPrivateId', '');
};
/**
 * Gets an object with the buttons and if they are shown or not.
 *
 * @param {object} mapObject - The map object.
 * @returns {object} The object containing the display status of each button or
 * an empty object if not found.
 * @throws {Error} If map is null or undefined.
 */
export const getSelectedButtons = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'selectedButtons', {});
};

/**
 * Checks if the video selected are to be shown.
 *
 * @param {object} mapObject - The map object.
 * @returns {string} False if buttons are not to be shown, true otherwise.
 * @throws {Error} If maps is null or undefined.
 */
export const getSettingsAdvancedMap = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'settingsAdvancedMap', {});
};

/**
 * Gets the map type.
 *
 * @param {object} mapObject - The map object.
 * @returns {string} The map type or empty string if not found.
 * @throws {Error} If map is null or undefined.
 */
export const getType = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'mapType', '');
};

/**
 * Checks if the map is a 3DCesium map.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True if the map is from 3DCesium, false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const isCesiumMap = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'mapType', '') === MAP_TYPES.CESIUM;
};

/**
 * Checks if the map is a 3DDeckGL map.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True if the map is from 3DDeckGL, false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const isDeckGLMap = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'mapType', '') === 'deckgl';
};

/**
 * Checks if the map is deleted.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True if the map is in trash, false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const isDeleted = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'deleted', '') !== 'no';
};

/**
 * Checks if the map is an esri map.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True if the map is from Esri, false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const isEsriMap = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'mapType', '') === 'esri';
};

/**
 * Checks if the map is a IAS map.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True if the map is from IAS, false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const isIASMap = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'mapType', '') === 'ias';
};

/**
 * Checks if the map is a 2DLeaflet map.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True if the map is from 2DLeaflet, false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const isLeafletMap = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'mapType', '') === MAP_TYPES.LEAFLET;
};

/**
 * Checks if the map is a 2DOpenLayers map.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True if the map is from 2DOpenLayers, false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const isOpenLayersMap = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'mapType', '') === 'openlayers';
};

/**
 * Check if map is shared.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True if the map is shared and false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const isShared = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'shared', false);
};
/**
 * Check if map is shared.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True if the map is shared and false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const isSharedPrivate = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'sharedPrivate', false);
};
/**
 * Check if map is location.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True if the map is location and false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const isWithLocation = mapObject => {
  if (isNil(mapObject)) throw new Error('location not defined');
  return get(mapObject, 'withLocation', false);
};
/**
 * Check if map is location private.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True if the map is location and false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const isWithLocationPrivate = mapObject => {
  if (isNil(mapObject)) throw new Error('location not defined');
  return get(mapObject, 'withLocationPrivate', false);
};

/**
 * Checks if the Attribution button should appear.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True to show the Attribution button, false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const showAttributionButton = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'selectedButtons.Attribution', false);
};
/**
 * Checks if the base map selector should appear.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True to show the base map selector, false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const showBaseMapSelector = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'selectedButtons.BaseMapSelector', false);
};

/**
 * Checks if the Collapse Button should appear.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True to show the Collapse Button, false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const showCollapseButton = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  const leftButtons = values(
    pick(mapObject.selectedButtons, ['Search', 'QRCode', 'Home', 'Zoom', 'Title'])
  );
  return includes(leftButtons, true);
};
/**
 * Checks if the base map selector should appear.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True to show the base map selector, false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const showDrawControl = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'selectedButtons.DrawControl', false);
};

/**
 * Checks if the Home button should appear.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True to show the Home button, false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const showHomeButton = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'selectedButtons.Home', false);
};
/**
 * Checks if the LayerEditor button should appear.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} False if buttons are not to be shown, true otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const showLayerEditor = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'selectedButtons.LayerEditor', false);
};
/**
 * Checks if the layer selector should appear.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True to show the layer selector, false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const showLayerSelector = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'selectedButtons.LayerSelector', false);
};
/**
 * Checks if the legend should appear.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True to show the legend, false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const showLegend = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'selectedButtons.Legend', false);
};

/**
 * Checks if the QR Code should appear.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True to show the QR Code, false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const showQRCode = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'selectedButtons.QRCode', false);
};
/**
 * Checks if the scale should appear.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True to show the scale, false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const showScale = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'selectedButtons.Scale', false);
};
/**
 * Checks if the Search button should appear.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True to show the Search button, false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const showSearchButton = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'selectedButtons.Search', false);
};

/**
 * Checks if the street view map is to be shown.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} False if buttons are not to be shown, true otherwise.
 * @throws {Error} If maps is null or undefined.
 */
export const showStreetView = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'showStreetView', true);
};
/**
 * Checks if the title button should appear.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True to show the title button, false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const showTitleButton = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'selectedButtons.Title', false);
};
/**
 * Checks if the map video is to be shown.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} False if buttons are not to be shown, true otherwise.
 * @throws {Error} If maps is null or undefined.
 */
export const showVideoEnable = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'videoEnableMaps', true);
};

/**
 * Checks if the zoom button should appear.
 *
 * @param {object} mapObject - The map object.
 * @returns {boolean} True to show the zoom button, false otherwise.
 * @throws {Error} If map is null or undefined.
 */
export const showZoomButton = mapObject => {
  if (isNil(mapObject)) throw new Error('map not defined');
  return get(mapObject, 'selectedButtons.Zoom', false);
};
